/**
 * useKanbanData — Hook para datos Kanban (0258)
 * Fetch: tags/kanban, tickets. Incluye agendaActive y profileName del response.
 */
import { useState, useEffect, useCallback } from "react";
import { listKanbanTags } from "../../../services/tags.api";
import { getKanbanTickets } from "../../../services/tickets.api";
import logger from "../../../utils/logger";

export default function useKanbanData() {
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [agendaActive, setAgendaActive] = useState(false);
  const [profileName, setProfileName] = useState(null);
  const [activeGroupName, setActiveGroupName] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      const response = await listKanbanTags();
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      setAgendaActive(Boolean(response.data.agendaActive));
      setProfileName(response.data.profileName ?? null);
      setActiveGroupName(response.data.activeGroupName ?? null);
      return fetchedTags;
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        return [];
      }
      logger.dashboard.error("Error cargando tags Kanban:", error);
      return [];
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await getKanbanTickets({ teste: true });
      setTickets(data.tickets || []);
      return data.tickets || [];
    } catch (err) {
      logger.dashboard.error("Error cargando tickets Kanban:", err);
      if (err.response && (err.response.status === 401 || err.response.status === 403)) return;
      setTickets([]);
    }
  }, []);

  const initializeData = useCallback(async () => {
    if (isInitialized) return;
    try {
      await fetchTags();
      await fetchTickets();
      setIsInitialized(true);
    } catch (error) {
      logger.dashboard.error("Error inicializando Kanban:", error);
    }
  }, [fetchTags, fetchTickets, isInitialized]);

  useEffect(() => {
    let mounted = true;
    const timer = setTimeout(() => {
      if (mounted && !isInitialized) initializeData();
    }, 500);
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [initializeData, isInitialized]);

  const refetch = useCallback(async () => {
    await fetchTags();
    await fetchTickets();
  }, [fetchTags, fetchTickets]);

  return {
    tags,
    setTags,
    tickets,
    setTickets,
    agendaActive,
    profileName,
    activeGroupName,
    isInitialized,
    refetch,
  };
}
